import {
  CoogoExecutionError,
  type CoogoCheckpointExactImpactOutputV1,
  type CoogoHandlerMap,
  type CoogoNodeHandlerContext,
  type CoogoOrchestrationNodeV2,
} from '@cogo/webmcp-runtime'
import type { StudioMcpBus } from '@/mcp/studio-mcp-bus'
import {
  consumeStudioUndoOwnershipReceipt,
  type StudioUndoOwnershipReceipt,
} from '@/mcp/undo-middleware'
import {
  consumeStudioCapabilityApprovalGrant,
  issueStudioCapabilityApprovalGrant,
} from './studio-capability-approval-grant'

export type StudioPlatformResumeAuthorityDecision =
  | {
      accepted: true
      actorId: string
      confirmationId: string
      approvedAt: string
    }
  | { accepted: false; code: string }

export interface StudioPlatformResumeAuthority {
  consumeLiveResumeAuthority(input: {
    planId: string
    runId: string
    projectId: string
    gateNodeId: string
    signal: AbortSignal
  }): Promise<StudioPlatformResumeAuthorityDecision>
}

export interface StudioOrchestrationNodeHandlerDependencies {
  bus: StudioMcpBus
  platformAuthority: StudioPlatformResumeAuthority
  /** Trusted adapter over an already authenticated durable run readback. */
  loadAuthenticatedN1Checkpoint(input: {
    planId: string
    runId: string
    projectId: string
    nodeId: 'n1-impact-before'
    admittedTarget: {
      kind: 'studio-page'
      projectId: string
      pageId: string
    }
    admittedPath: string
    signal: AbortSignal
  }): Promise<unknown | null>
  /** Durable recovery must mark an observed running N4 as unknown outcome. */
  recoveredRunningNode: (context: CoogoNodeHandlerContext) => boolean
}

type ExactImpactRaw = {
  observations: Array<Record<string, unknown>>
  verified: boolean
  fingerprint: string
  capturedAt: string
}

const EXACT_READBACK = 'cogo.studio.capability.readbackExactImpact'
const UPDATE_PAGE_META = 'cogo.studio.updatePageMeta'
const UNDO = 'cogo.studio.undo'

function fail(message: string, code: string): never {
  throw new CoogoExecutionError(message, code)
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function hasExactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  const actual = Object.keys(value).sort()
  const expected = [...keys].sort()
  return actual.length === expected.length &&
    actual.every((key, index) => key === expected[index])
}

function assertNotAborted(signal: AbortSignal): void {
  if (signal.aborted) throw signal.reason ?? new DOMException('Aborted', 'AbortError')
}

function checkpointObservation(observation: Record<string, unknown>) {
  const path = String(observation.path ?? '')
  if (!path) fail('Exact impact readback omitted its path.', 'exact_impact_invalid')
  if (observation.presence === false) return { path, presence: 'absent' as const }
  if (observation.presence !== true) {
    fail('Exact impact readback returned invalid presence.', 'exact_impact_invalid')
  }
  if (observation.value === undefined) {
    return { path, presence: 'present' as const, valueKind: 'undefined' as const }
  }
  if (
    observation.value !== null &&
    !['string', 'number', 'boolean'].includes(typeof observation.value)
  ) {
    fail('Exact impact readback returned a non-primitive value.', 'exact_impact_invalid')
  }
  return {
    path,
    presence: 'present' as const,
    valueKind: 'json-primitive' as const,
    value: observation.value as string | number | boolean | null,
  }
}

function checkpointExactImpact(
  raw: ExactImpactRaw,
  mode: 'capture' | 'assert',
): CoogoCheckpointExactImpactOutputV1 {
  if (
    raw.verified !== true ||
    !raw.fingerprint?.trim() ||
    !raw.capturedAt?.trim() ||
    !Array.isArray(raw.observations) ||
    raw.observations.length === 0
  ) {
    fail('Exact impact readback was incomplete.', 'exact_impact_invalid')
  }
  return {
    kind: 'exact-impact',
    mode,
    observations: raw.observations.map(checkpointObservation),
    verified: true,
    fingerprint: raw.fingerprint,
    capturedAt: raw.capturedAt,
  }
}

async function executeReadback(
  bus: StudioMcpBus,
  params: Record<string, unknown>,
  signal: AbortSignal,
): Promise<CoogoCheckpointExactImpactOutputV1> {
  assertNotAborted(signal)
  const result = await bus.execute(
    { tool: EXACT_READBACK, params },
    { signal },
  )
  if (!result.success) {
    fail(result.error ?? 'Exact impact readback failed.', 'exact_impact_unavailable')
  }
  return checkpointExactImpact(
    result.data as ExactImpactRaw,
    params.mode === 'capture' ? 'capture' : 'assert',
  )
}

function beforePath(before: CoogoCheckpointExactImpactOutputV1): string {
  const path = before.observations[0]?.path
  if (!path) fail('Before readback is unavailable.', 'manual_recovery_required')
  return path
}

function exactObservationEqual(
  left: CoogoCheckpointExactImpactOutputV1,
  right: CoogoCheckpointExactImpactOutputV1,
): boolean {
  return JSON.stringify(left.observations) === JSON.stringify(right.observations)
}

function proposedObservation(path: string, proposed: unknown): string {
  if (
    proposed !== null &&
    !['string', 'number', 'boolean'].includes(typeof proposed)
  ) {
    fail('Proposed exact impact is not a primitive.', 'manual_recovery_required')
  }
  return JSON.stringify([
    {
      path,
      presence: 'present',
      valueKind: 'json-primitive',
      value: proposed,
    },
  ])
}

function gateNode(context: CoogoNodeHandlerContext): CoogoOrchestrationNodeV2 {
  const node = context.plan.nodes.find((candidate) => candidate.id === 'n2-human-gate')
  if (!node || context.plan.schemaVersion !== 'coogo.orchestration-plan.v2') {
    fail('Bound durable human gate is unavailable.', 'resume_authority_invalid')
  }
  return node as CoogoOrchestrationNodeV2
}

function validateAuthenticatedN1Checkpoint(input: {
  value: unknown
  context: CoogoNodeHandlerContext
}): CoogoCheckpointExactImpactOutputV1 {
  const gate = gateNode(input.context).humanGate
  if (!gate || gate.target.projectId !== input.context.plan.projectId) {
    fail('Durable admission target is invalid.', 'checkpoint_authority_mismatch')
  }
  const admittedPath = `/manifest/pages/${encodeURIComponent(gate.target.pageId)}/title`
  const envelope = record(input.value)
  if (
    !envelope ||
    !hasExactKeys(envelope, [
      'planId',
      'runId',
      'projectId',
      'nodeId',
      'admittedTarget',
      'output',
    ]) ||
    envelope.planId !== input.context.plan.planId ||
    envelope.runId !== input.context.plan.runId ||
    envelope.projectId !== input.context.plan.projectId ||
    envelope.nodeId !== 'n1-impact-before'
  ) {
    fail('Durable N1 checkpoint scope does not match this run.', 'checkpoint_authority_mismatch')
  }
  const target = record(envelope.admittedTarget)
  if (
    !target ||
    !hasExactKeys(target, ['kind', 'projectId', 'pageId']) ||
    target.kind !== 'studio-page' ||
    target.projectId !== gate.target.projectId ||
    target.pageId !== gate.target.pageId
  ) {
    fail('Durable N1 checkpoint target is not admitted.', 'checkpoint_authority_mismatch')
  }
  const output = record(envelope.output)
  if (
    !output ||
    !hasExactKeys(output, [
      'kind',
      'mode',
      'observations',
      'verified',
      'fingerprint',
      'capturedAt',
    ]) ||
    output.kind !== 'exact-impact' ||
    output.mode !== 'capture' ||
    output.verified !== true ||
    typeof output.fingerprint !== 'string' ||
    !output.fingerprint.trim() ||
    typeof output.capturedAt !== 'string' ||
    !Number.isFinite(Date.parse(output.capturedAt)) ||
    !Array.isArray(output.observations) ||
    output.observations.length !== 1
  ) {
    fail('Durable N1 checkpoint output is invalid.', 'checkpoint_authority_mismatch')
  }
  const observation = record(output.observations[0])
  const absent = observation?.presence === 'absent'
  const presentUndefined =
    observation?.presence === 'present' && observation.valueKind === 'undefined'
  const presentPrimitive =
    observation?.presence === 'present' &&
    observation.valueKind === 'json-primitive' &&
    (observation.value === null ||
      typeof observation.value === 'string' ||
      typeof observation.value === 'boolean' ||
      (typeof observation.value === 'number' &&
        Number.isFinite(observation.value)))
  const validObservationKeys = observation && (
    (absent && hasExactKeys(observation, ['path', 'presence'])) ||
    (presentUndefined &&
      hasExactKeys(observation, ['path', 'presence', 'valueKind'])) ||
    (presentPrimitive &&
      hasExactKeys(observation, ['path', 'presence', 'valueKind', 'value']))
  )
  if (
    !observation ||
    !validObservationKeys ||
    observation.path !== admittedPath
  ) {
    fail('Durable N1 checkpoint path is not admitted.', 'checkpoint_authority_mismatch')
  }
  return structuredClone(output) as unknown as CoogoCheckpointExactImpactOutputV1
}

export function createStudioOrchestrationNodeHandlers(
  input: StudioOrchestrationNodeHandlerDependencies,
): CoogoHandlerMap {
  const beforeByRun = new Map<string, CoogoCheckpointExactImpactOutputV1>()
  const ownershipByRun = new Map<string, StudioUndoOwnershipReceipt>()

  const readback: NonNullable<CoogoHandlerMap['readback']> = async (context) => {
    const operation = context.node.operation
    if (operation?.name !== EXACT_READBACK) {
      fail(`Unsupported Studio readback ${operation?.name ?? '<missing>'}.`, 'handler_not_registered')
    }
    if (context.node.id.startsWith('r2-verify-')) {
      const before = beforeByRun.get(context.plan.runId)
      if (!before) fail('Before readback is unavailable after recovery.', 'manual_recovery_required')
      const observation = before.observations[0]
      const params = observation.presence === 'absent'
        ? { mode: 'assert', checks: [{ path: observation.path, expectation: 'absent' }] }
        : {
            mode: 'assert',
            checks: [{
              path: observation.path,
              expectation: 'equals',
              expected: observation.valueKind === 'undefined' ? undefined : observation.value,
            }],
          }
      return { output: await executeReadback(input.bus, params, context.signal) }
    }
    const output = await executeReadback(
      input.bus,
      operation.params ?? {},
      context.signal,
    )
    if (context.node.id === 'n1-impact-before') beforeByRun.set(context.plan.runId, output)
    return { output }
  }

  const compensate = async (context: CoogoNodeHandlerContext) => {
    assertNotAborted(context.signal)
    const before = beforeByRun.get(context.plan.runId) ??
      (context.dependencyOutputs['n1-impact-before'] as CoogoCheckpointExactImpactOutputV1 | undefined)
    if (!before) fail('Before readback is unavailable.', 'manual_recovery_required')
    const path = beforePath(before)
    const compensation = record(context.node.input?.compensation)
    const preReadback = record(compensation?.preReadback)
    const proposed = preReadback?.proposed
    let current: CoogoCheckpointExactImpactOutputV1
    try {
      current = await executeReadback(
        input.bus,
        { mode: 'capture', paths: [path] },
        context.signal,
      )
    } catch (error) {
      fail(
        `Manual recovery required because compensation readback was unavailable: ${error instanceof Error ? error.message : String(error)}`,
        'manual_recovery_required',
      )
    }
    if (exactObservationEqual(current, before)) {
      return {
        output: {
          kind: 'compensation-result' as const,
          disposition: 'already-restored' as const,
          verified: true as const,
        },
      }
    }
    if (JSON.stringify(current.observations) !== proposedObservation(path, proposed)) {
      fail('Manual recovery required because exact impact diverged.', 'manual_recovery_required')
    }
    const receipt = ownershipByRun.get(context.plan.runId)
    if (!receipt || receipt.capabilityPlanId !== context.plan.planId) {
      fail('Manual recovery required because owned undo receipt is missing.', 'manual_recovery_required')
    }
    const undone = await input.bus.execute(
      {
        tool: UNDO,
        params: {
          _expectedCapabilityPlanId: receipt.capabilityPlanId,
          _expectedUndoEntryId: receipt.undoEntryId,
          _discardRedo: true,
        },
      },
      { signal: context.signal },
    )
    if (!undone.success) {
      fail(
        `Manual recovery required because owned undo was refused: ${undone.error ?? 'unknown'}`,
        'manual_recovery_required',
      )
    }
    ownershipByRun.delete(context.plan.runId)
    return {
      output: {
        kind: 'compensation-result' as const,
        disposition: 'owned-undo' as const,
        verified: true as const,
      },
    }
  }

  const tool: NonNullable<CoogoHandlerMap['tool']> = async (context) => {
    if (context.node.operation?.name === UNDO) return compensate(context)
    if (context.node.operation?.name !== UPDATE_PAGE_META) {
      fail(`Unsupported Studio tool ${context.node.operation?.name ?? '<missing>'}.`, 'handler_not_registered')
    }
    let before = beforeByRun.get(context.plan.runId)
    if (!before) {
      assertNotAborted(context.signal)
      const gate = gateNode(context).humanGate
      if (!gate) {
        fail('Bound durable human gate is unavailable.', 'checkpoint_authority_mismatch')
      }
      const admittedPath = `/manifest/pages/${encodeURIComponent(gate.target.pageId)}/title`
      const seed = await input.loadAuthenticatedN1Checkpoint({
        planId: context.plan.planId,
        runId: context.plan.runId,
        projectId: context.plan.projectId,
        nodeId: 'n1-impact-before',
        admittedTarget: structuredClone(gate.target),
        admittedPath,
        signal: context.signal,
      })
      if (seed) {
        before = validateAuthenticatedN1Checkpoint({ value: seed, context })
        beforeByRun.set(context.plan.runId, before)
      }
    }
    if (!before) {
      fail('N4 outcome is unknown after recovery; manual readback is required.', 'manual_recovery_required')
    }
    if (input.recoveredRunningNode(context)) {
      await executeReadback(
        input.bus,
        { mode: 'capture', paths: [beforePath(before)] },
        context.signal,
      )
      fail('Recovered running N4 has an unknown mutation outcome.', 'manual_recovery_required')
    }
    const reasserted = await executeReadback(
      input.bus,
      { mode: 'capture', paths: [beforePath(before)] },
      context.signal,
    )
    if (!exactObservationEqual(reasserted, before)) {
      fail('The approved exact impact is stale.', 'CONFIRMATION_STALE')
    }
    const authority = await input.platformAuthority.consumeLiveResumeAuthority({
      planId: context.plan.planId,
      runId: context.plan.runId,
      projectId: context.plan.projectId,
      gateNodeId: 'n2-human-gate',
      signal: context.signal,
    })
    const gate = gateNode(context).humanGate
    if (
      !authority.accepted ||
      !gate ||
      authority.confirmationId !== gate.confirmationId ||
      !authority.actorId.trim() ||
      !authority.approvedAt.trim()
    ) {
      fail(
        `Platform resume authority rejected${authority.accepted ? '' : `: ${authority.code}`}.`,
        'resume_authority_rejected',
      )
    }
    assertNotAborted(context.signal)
    const params = {
      ...(context.node.operation.params ?? {}),
      _capabilityPlanId: context.plan.planId,
    }
    const approvalToken = issueStudioCapabilityApprovalGrant({
      tool: UPDATE_PAGE_META,
      params,
      projectId: context.plan.projectId,
      actorId: authority.actorId,
      confirmationId: authority.confirmationId,
      approvedAt: authority.approvedAt,
    })
    let result
    try {
      result = await input.bus.executeWebMcp(
        {
          tool: UPDATE_PAGE_META,
          params: { ...params, _capabilityApprovalToken: approvalToken },
        },
        { signal: context.signal },
      )
    } finally {
      try {
        consumeStudioCapabilityApprovalGrant({
          token: approvalToken,
          tool: UPDATE_PAGE_META,
          params: { ...params, _capabilityApprovalToken: approvalToken },
          projectId: context.plan.projectId,
          failureScope: 'studio',
        })
      } catch {
        // The WebMCP boundary consumed the exact single-use capability grant.
      }
    }
    if (!result.success) {
      fail(result.error ?? 'Studio page mutation failed.', 'studio_tool_failed')
    }
    const ownership = consumeStudioUndoOwnershipReceipt(result)
    if (!ownership || ownership.capabilityPlanId !== context.plan.planId) {
      fail('Page mutation did not return owned undo authority.', 'undo_ownership_missing')
    }
    ownershipByRun.set(context.plan.runId, ownership)
    return { output: { ok: true } }
  }

  return { readback, tool }
}
